import React, { useEffect, useRef } from 'react';
import { Button } from 'antd';
import {
  GraduationCapIcon,
  GamepadIcon,
  ChartIcon,
  HandshakeIcon,
  GlobeIcon,
  FlaskIcon,
  CheckIcon,
  StarIcon,
  SparkIcon,
} from './icons/SketchIcons';
import './Initiatives.css';

const PROJECTS = [
  {
    icon: GraduationCapIcon,
    accent: 'blue',
    title: 'Personalized Learning Pathways',
    body:
      'Developing adaptive learning algorithms that customize educational experiences based on individual student needs and learning styles.',
    details: [
      'Machine learning-based content recommendation',
      'Real-time difficulty adjustment',
      'Learning style detection and adaptation',
    ],
  },
  {
    icon: GamepadIcon,
    accent: 'red',
    title: 'Gamification in STEM Education',
    body:
      'Researching the impact of game mechanics and interactive simulations on student engagement and learning outcomes in science education.',
    details: [
      'Behavioral data collection and analysis',
      'Engagement metrics development',
      'Game design best practices for education',
    ],
  },
  {
    icon: ChartIcon,
    accent: 'green',
    title: 'Learning Analytics',
    body:
      'Building comprehensive analytics tools to help educators understand student progress and identify areas needing intervention.',
    details: [
      'Predictive models for student success',
      'Real-time performance dashboards',
      'Intervention recommendation systems',
    ],
  },
  {
    icon: HandshakeIcon,
    accent: 'purple',
    title: 'Collaborative Learning Environments',
    body:
      'Designing and testing collaborative features that enhance peer learning and group problem-solving in virtual environments.',
    details: [
      'Multi-user simulation frameworks',
      'Peer assessment tools',
      'Communication pattern analysis',
    ],
  },
  {
    icon: GlobeIcon,
    accent: 'yellow',
    title: 'Accessibility in STEM',
    body:
      'Ensuring equitable access to quality STEM education through inclusive design and accessibility research.',
    details: [
      'Universal design principles',
      'Assistive technology integration',
      'Multilingual content support',
    ],
  },
  {
    icon: FlaskIcon,
    accent: 'teal',
    title: 'Virtual Laboratory Development',
    body:
      'Creating immersive virtual lab experiences that provide hands-on learning opportunities for students without physical lab access.',
    details: [
      'Physics and chemistry simulations',
      'Safety training modules',
      'Experiment design tools',
    ],
  },
];

const Initiatives = () => {
  const rootRef = useRef(null);

  // Reveal elements as they scroll into view (respects reduced-motion).
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const items = Array.from(root.querySelectorAll('.reveal'));
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduce || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );

    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="initiatives-page" ref={rootRef}>
      <div className="container">
        <header className="initiatives-header reveal">
          <span className="header-doodle header-doodle-left" aria-hidden="true">
            <StarIcon size={28} />
          </span>
          <span className="header-doodle header-doodle-right" aria-hidden="true">
            <SparkIcon size={30} />
          </span>
          <h1 className="scribble-underline">Research Initiatives</h1>
          <p className="subtitle">
            Advancing STEM education through innovative research and collaboration
          </p>
        </header>

        <section className="initiative-section">
          <h2 className="section-title reveal">Current Projects</h2>
          <div className="initiatives-grid">
            {PROJECTS.map(({ icon: Icon, accent, title, body, details }, i) => (
              <article
                className={`initiative-card reveal accent-${accent}`}
                key={title}
                style={{ '--i': i }}
              >
                <span className="card-tape" aria-hidden="true" />
                <div className="initiative-icon">
                  <Icon size={36} />
                </div>
                <h3>{title}</h3>
                <p>{body}</p>
                <ul className="initiative-details">
                  {details.map((d) => (
                    <li key={d}>
                      <span className="check-mark" aria-hidden="true">
                        <CheckIcon size={16} />
                      </span>
                      <span>{d}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section className="collaboration-section">
          <h2 className="section-title reveal">Partnerships &amp; Collaborations</h2>
          <div className="collaboration-grid">
            <div className="collaboration-card reveal" style={{ '--i': 0 }}>
              <h4>Educational Institutions</h4>
              <p>Partnering with K-12 schools and universities to pilot and evaluate new learning technologies.</p>
            </div>
            <div className="collaboration-card reveal" style={{ '--i': 1 }}>
              <h4>Research Organizations</h4>
              <p>Collaborating with educational research institutions to advance the science of learning.</p>
            </div>
            <div className="collaboration-card reveal" style={{ '--i': 2 }}>
              <h4>Technology Partners</h4>
              <p>Working with EdTech companies to integrate cutting-edge tools and platforms.</p>
            </div>
          </div>
        </section>

        <section className="publications-section">
          <h2 className="section-title reveal">Recent Publications</h2>
          <div className="publications-list">
            <div className="publication-item reveal" style={{ '--i': 0 }}>
              <h4>Adaptive Difficulty in Educational Games: A Meta-Analysis</h4>
              <p className="publication-meta">Journal of Educational Technology • 2025</p>
              <p className="publication-abstract">
                Analysis of how dynamic difficulty adjustment impacts learning outcomes
                and engagement across different STEM domains.
              </p>
            </div>
            <div className="publication-item reveal" style={{ '--i': 1 }}>
              <h4>Behavioral Patterns in Interactive Physics Simulations</h4>
              <p className="publication-meta">Science Education Research • 2025</p>
              <p className="publication-abstract">
                Large-scale study of student interaction patterns in physics simulations
                and their correlation with conceptual understanding.
              </p>
            </div>
            <div className="publication-item reveal" style={{ '--i': 2 }}>
              <h4>Privacy-Preserving Learning Analytics in K-12 Education</h4>
              <p className="publication-meta">International Conference on Learning Analytics • 2024</p>
              <p className="publication-abstract">
                Framework for collecting and analyzing student data while maintaining
                FERPA and COPPA compliance.
              </p>
            </div>
          </div>
        </section>

        <section className="get-involved-section reveal">
          <h2>Get Involved</h2>
          <p>
            Interested in collaborating on educational research or implementing PING
            in your institution? We'd love to hear from you.
          </p>
          <div className="cta-buttons">
            <Button type="primary" size="large">Contact Research Team</Button>
            <Button size="large">Join Mailing List</Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Initiatives;
